import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Database, Settings, Package, Users, Copy, List, Sparkles,
  FileClock, TrendingUp, Award, UserPlus, Layers, Clock, FileAudio, Wallet, Calculator, Tag,
  CalendarCheck, BadgePercent, ShieldCheck, GraduationCap, Activity, CalendarDays, BookOpen,
  AlertTriangle, UserCog, Download as DownloadIcon, MousePointerClick,
} from "lucide-react";
import { Compass } from "lucide-react";
import { BarChart3 } from "lucide-react";
import { ArrowRightLeft } from "lucide-react";
import { Mailbox } from "lucide-react";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { adminApi } from "../api";
import StuckPaymentsAlert from "../components/StuckPaymentsAlert";
import PaymentWebhooksHealth from "../components/PaymentWebhooksHealth";
import OrdersQuickSearch from "../components/orders/OrdersQuickSearch";

const EUR = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

function StateRow({ s }) {
  const singular = s.nr === 1;
  const verb = singular ? "Existe 1" : `Existem ${s.nr.toLocaleString("pt-PT")}`;
  const noun = singular ? "encomenda" : "encomendas";
  return (
    <li className="flex items-center gap-3 py-2 border-b border-[#F3F0E7] last:border-0" data-testid={`state-row-${s.state_id}`}>
      <span className="text-xs uppercase tracking-[0.12em] font-mono text-[#978D78] w-9 shrink-0">{s.state_id}</span>
      <div className="flex-1 text-sm">
        <span>{verb}</span>
        {s.total_com_iva !== null && s.total_com_iva !== undefined && (
          <span className="ml-1.5 text-[#4B5563] font-mono">[{EUR(s.total_com_iva)}]</span>
        )}
        <span> {noun} em </span>
        <span className="font-semibold text-[#0F1829]">«{s.state_label}»</span>.
      </div>
      <Link
        to={`/admin/dashboard/orders?state=${s.state_id}`}
        data-testid={`state-link-${s.state_id}`}
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-[#E6E1D6] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition"
      >
        Ver →
      </Link>
    </li>
  );
}

function DashCard({ to, icon: Icon, title, subtitle, disabled = false, testid }) {
  const inner = (
    <div className={`p-4 border border-[#E6E1D6] rounded-md bg-white h-full transition ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#0F1829] hover:shadow-sm cursor-pointer'}`}
         data-testid={testid}>
      <div className="flex items-center gap-3 mb-1.5">
        <div className="w-9 h-9 grid place-items-center rounded bg-[#FAF8F2] text-[#0F1829] border border-[#E6E1D6]"><Icon size={17} /></div>
        <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-[#0F1829]">{title}</h3>
      </div>
      <p className="text-xs text-[#6B7280] leading-relaxed">{subtitle}</p>
      {disabled && <p className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] mt-2">Brevemente</p>}
    </div>
  );
  return disabled ? <div>{inner}</div> : <Link to={to} className="block">{inner}</Link>;
}

export default function AdminHome() {
  const { user } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.ordersSummary()
      .then((r) => setSummary(r.data || []))
      .catch((e) => setErr(e.message || "Erro"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-home" className="max-w-6xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Database size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Bem-vindo, {user?.name || ""}</h1>
          <p className="text-sm text-[#4B5563]">Painel de manutenção operacional.</p>
        </div>
      </div>

      {/* Alerta: encomendas com URL de pagamento em falta (auto-hide quando vazio) */}
      <StuckPaymentsAlert />

      {/* Saúde dos webhooks de pagamento — coexistência VM GCP / app nova */}
      <PaymentWebhooksHealth />

      {/* Pesquisa rápida — fora dos estados, procura em todos */}
      <OrdersQuickSearch />

      {/* Tratar Encomendas */}
      <section className="mb-8" data-testid="card-orders-workflow">
        <div className="p-5 bg-white border border-[#E6E1D6] rounded-md">
          <div className="flex items-center gap-2 mb-3">
            <Package size={18} className="text-[#0F1829]" />
            <h2 className="text-base font-bold uppercase tracking-[0.12em]">Tratar Encomendas</h2>
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          {loading && <p className="text-sm text-[#6B7280]">A carregar...</p>}
          {!loading && summary && (
            <ul className="divide-y divide-[#F3F0E7]">
              {summary.map((s) => <StateRow key={s.state_id} s={s} />)}
            </ul>
          )}
        </div>
      </section>

      {/* Operações */}
      <section className="mb-8">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2">Operações</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DashCard to="/admin/operations/change-parent" icon={Users} title="Mudar Pai de Cliente"
            subtitle="Altera o upline de um cliente na rede (confirmação antes de processar)." testid="card-change-parent" />
          <DashCard to="/admin/operations/copy-product" icon={Copy} title="Copiar Produto"
            subtitle="Duplica produto + campanhas + ficheiros + cursos + áreas + comissões + ofertas." testid="card-copy-product" />
          <DashCard to="/admin/media-summaries" icon={FileAudio} title="Resumos Media Center"
            subtitle="Gera resumos automáticos (até 950 chars) para áudios e vídeos com Whisper + Gemini 3 Flash." testid="card-media-summaries" />
          <DashCard to="/admin/dreams" icon={Sparkles} title="IHTP Dreams — Métricas"
            subtitle="Subscritores activos, MRR, sonhos criados, top sonhadores, conversões de share." testid="card-dreams-metrics" />
          <DashCard to="/admin/coach-virtual" icon={Compass} title="Coach Virtual — Adesão"
            subtitle="Clientes que preencheram a Roda da Vida por mês e acumulado, com gráficos." testid="card-coach-virtual" />
          <DashCard to="/admin/jobs" icon={Activity} title="Cron Jobs"
            subtitle="Visibilidade + execução manual dos 31 jobs automatizados que substituem o GCP Cloud Scheduler." testid="card-jobs" />
          <DashCard to="/admin/operations/commission-payouts" icon={Wallet} title="Pagamentos de Comissões"
            subtitle="Regista pagamentos em lote aos consultores com valor por pagar." testid="card-commission-payouts" />
          <DashCard to="/admin/operations/manual-commission-adjustment" icon={Calculator} title="Acerto Manual de Comissões"
            subtitle="Lança comissão ou créditos directamente na conta-corrente do cliente, sem limite." testid="card-manual-commission-adjustment" />
          <DashCard to="/admin/vales" icon={ArrowRightLeft} title="Transferir Vales"
            subtitle="Transfere um valor em vales de um cliente para outro (retira do saldo da origem e cria vale no destino)." testid="card-transfer-vales" />
          <DashCard to="/admin/moosend-maps" icon={Mailbox} title="Produtos → Mailing List Moosend"
            subtitle="Associa/edita a ligação de um produto (ou pack/tipo) à mailing list Moosend que activa a automação ao pagar." testid="card-moosend-maps" />
          <DashCard to="/admin/operations/confirm-clients" icon={ShieldCheck} title="Confirmar Registos de Clientes"
            subtitle="Marca em massa clientes activos com IsConfirmed=0 como confirmados (legacy webpages_Membership)." testid="card-confirm-clients" />
          <DashCard to="/admin/operations/import-clients" icon={UserPlus} title="Importar Clientes"
            subtitle="Carrega vários clientes de um Excel, associados a um patrocinador." testid="card-import-clients" />
          <DashCard to="/admin/marketing" icon={BarChart3} title="Avaliação de Marketing"
            subtitle="ROA por campanha e ROI global de marketing." testid="card-marketing" />
          <DashCard to="/admin/crm-botoes" icon={MousePointerClick} title="Botões do CRM"
            subtitle="Chips de acção rápida do modal &quot;Evoluir Lead&quot; (Enviei email, Reunião agendada, Não compareceu...). Alterações reflectem imediatamente no CRM." testid="card-crm-botoes" />
          <DashCard to="/admin/downloads" icon={DownloadIcon} title="Documentos de Download"
            subtitle="Adicionar/editar/eliminar os PDFs, flyers e ferramentas visíveis em `/my/downloads` (upload directo para o CDN)." testid="card-downloads" />
          <DashCard to="/admin/lead-intelligence" icon={Sparkles} title="Inteligência da Lead"
            subtitle="Pipeline IA (Claude Sonnet 4.5) que enriquece e classifica cada lead nova. Ver estatísticas e reprocessar." testid="card-lead-intelligence" />
          <DashCard to="/admin/packs" icon={Layers} title="Manutenção de Packs"
            subtitle="CRUD de packs (subscrições, promoções, Success Box): preços, IVA, composição de produtos, periodicidade." testid="card-packs" />
          <DashCard to="/admin/precarios" icon={Tag} title="Preçários B2B"
            subtitle="Gere tabelas de preços versionadas por ano (cursos, palestras, workshops, consultoria)." testid="card-precarios" />
          <DashCard to="/admin/reports/new-pins" icon={UserPlus} title="Novos PINs de Consultor"
            subtitle="Consultores com nova inscrição este mês." testid="card-new-pins" />
          <DashCard to="/admin/black-month" icon={BadgePercent} title="Campanha Black Month"
            subtitle="Define o período e os produtos da promoção de coaching «compra 4, paga 3»." testid="card-black-month" />
        </div>
      </section>

      {/* Cursos */}
      <section className="mb-8" data-testid="section-cursos">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2 flex items-center gap-1.5">
          <GraduationCap size={13} /> Cursos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DashCard to="/admin/cursos/situacao" icon={List} title="Situação dos Cursos"
            subtitle="Lista de todos os cursos com pesquisa por nº, produto, nome, datas e estado; acesso ao detalhe." testid="card-cursos-situacao" />
          <DashCard to="/admin/cursos/inscricoes" icon={GraduationCap} title="Inscrições em Cursos"
            subtitle="Gere fichas de inscrição por curso e templates DOCX (Ficha, Contrato, Decl. Menores)." testid="card-curso-inscricoes" />
          <DashCard to="/admin/inscricoes-pendentes" icon={AlertTriangle} title="Inscrições com email pendente"
            subtitle="Monitoriza em tempo real links cujo email automático ao comprador ainda não foi enviado (safety-net SMTP)." testid="card-inscricoes-pendentes" />
          <DashCard to="/admin/cursos/sessoes" icon={CalendarDays} title="Sessões dos Cursos"
            subtitle="Plano de sessões pedagógicas (Introdução, Desenvolvimento, Conclusão) com import Excel." testid="card-curso-sessoes" />
          <DashCard to="/admin/cursos/edit" icon={BookOpen} title="Editar Cursos"
            subtitle="Editor amigável com tabs (Geral, Empresa B2B, Datas DTP, Avaliação) para todos os campos." testid="card-curso-edit" />
          <DashCard to="/admin/cursos/copiar" icon={Copy} title="Copiar Curso"
            subtitle="Copia conteúdo e DTP de um curso para outro existente (mantém datas; não copia presenças, notas, contratos nem inscrições)." testid="card-copiar-curso" />
          <DashCard to="/admin/cursos/import-base" icon={Database} title="Importar Base de Cursos"
            subtitle="Carrega o NOVA-BASE-BD.xlsx (Público + Empresas) para criar/actualizar cursos em massa." testid="card-import-base-cursos" />
          <DashCard to="/admin/formadores" icon={UserCog} title="Manutenção de Formadores"
            subtitle="Biblioteca central de formadores: habilitações, CV, contactos, IBAN e histórico de cursos." testid="card-formadores" />
          <DashCard to="/admin/tipos-certificacao" icon={Award} title="Tipos de Certificação"
            subtitle="Configuração dos tipos de certificação (PNL, Coaching, ...) e documentos obrigatórios que os formandos têm de submeter." testid="card-tipos-certificacao" />
        </div>
      </section>

      {/* Listagens */}
      <section className="mb-8">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2">Listagens</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DashCard to="/admin/reports/clients-by-product" icon={List} title="Clientes por Produto"
            subtitle="Lista de clientes que compraram um produto." testid="card-report-clients-product" />
          <DashCard to="/admin/reports/clients-by-pack" icon={Layers} title="Clientes por Pack"
            subtitle="Lista de clientes que compraram um pack." testid="card-report-clients-pack" />
          <DashCard to="/admin/reports/commissions-client" icon={TrendingUp} title="Posição de Comissões"
            subtitle="Posição actual das comissões de um cliente." testid="card-report-comm" />
          <DashCard to="/admin/reports/consultant-levels" icon={Award} title="Níveis de Negócio"
            subtitle="Níveis de negócio dos consultores." testid="card-report-levels" />
          <DashCard to="/admin/reports/new-consultant-levels" icon={Sparkles} title="Novos Níveis"
            subtitle="Novos níveis de negócio dos consultores." testid="card-report-new-levels" />
          <DashCard to="/admin/reports/clients-volume" icon={TrendingUp} title="Volume por Cliente"
            subtitle="Volume total de encomendas por cliente." testid="card-report-volume" />
          <DashCard to="/admin/reports/active-subscriptions" icon={Clock} title="Subscrições Activas"
            subtitle="Clientes com subscrições activas." testid="card-report-subs" />
          <DashCard to="/admin/dashboard/consultant-renewals" icon={CalendarCheck} title="Renovações de Consultores"
            subtitle="Consultores activos que renovaram contrato, por ano e trimestre, com anos completos como consultor." testid="card-consultant-renewals" />
          <DashCard to="/admin/t/encomenda/Tinf_CodigoPromocional" icon={BadgePercent} title="Códigos Promocionais"
            subtitle="Gere os códigos promocionais usados em encomendas (criar, editar, desactivar)." testid="card-promo-codes" />
          <DashCard to="/admin/audit" icon={FileClock} title="Log de Auditoria"
            subtitle="Histórico de alterações feitas aqui no Admin DB." testid="card-audit" />
        </div>
      </section>

      <div className="p-4 bg-[#FAF8F2] border border-[#E6E1D6] rounded-md flex items-start gap-2.5">
        <Settings size={14} className="text-[#978D78] mt-0.5 shrink-0" />
        <p className="text-xs text-[#6B7280] leading-relaxed">
          Tabelas disponíveis no painel lateral. Alterações ficam em log de auditoria. Sessão independente do CRM/Media/MY.
        </p>
      </div>
    </div>
  );
}
